import type {
	T3pResource,
	TProductDataRegions,
	TProductEdition,
	TSearch3pConfigAPIResponse,
	TSearchConfigAPIResponse,
} from '@atlassian/search-client';

export type SearchCombinedFirstPartyProduct = {
	name: string;
};

export type SearchCombinedThirdPartyProduct = {
	name: string;
	outboundAuthUrl: string;
	isUserOAuthed: boolean;
	providerId?: string | null;
	integrationARI?: string | null;
	workspaceName?: string | null;
	workspaceUrl?: string | null;
	datasourceId?: string | null;
	connectionId?: string | null;
	isImpersonationAuthEnabled?: boolean | null;
	lightIcon?: string | null;
	entityTypes?: string[] | null;
	environmentType?: string | null;
};

export type SearchCombinedSiteProductInfo = {
	name?: string | null;
	dataRegion?: string[] | string | null;
};

export type SearchCombinedSiteMetadata = {
	isRovoEnabled?: boolean;
	isJiraIssueIndexed?: boolean;
	applicationMode?: string | null;
	siteName?: string | null;
	orgId?: string | null;
	productEdition?: string | null;
	productInfo?: SearchCombinedSiteProductInfo[] | null;
};

export type SearchCombinedConfigPayload = {
	firstPartyProducts?: SearchCombinedFirstPartyProduct[];
	thirdPartyProducts?: SearchCombinedThirdPartyProduct[];
	intercomHmac?: string | null;
	siteMetadata?: SearchCombinedSiteMetadata | null;
};

const mapProductInfoToDataRegions = (
	productInfo?: SearchCombinedSiteProductInfo[] | null,
): TProductDataRegions | undefined => {
	if (!productInfo || productInfo.length === 0) {
		return undefined;
	}

	return productInfo.reduce<TProductDataRegions>((acc, info) => {
		if (!info?.name || !info.dataRegion) {
			return acc;
		}

		acc[info.name] = Array.isArray(info.dataRegion) ? info.dataRegion : [info.dataRegion];
		return acc;
	}, {});
};

const mapProductEdition = (
	productEdition?: string | null,
	firstPartyProducts?: SearchCombinedFirstPartyProduct[],
): TProductEdition | undefined => {
	if (!productEdition || !firstPartyProducts || firstPartyProducts.length === 0) {
		return undefined;
	}

	return firstPartyProducts.reduce<TProductEdition>((acc, product) => {
		acc[product.name] = productEdition;
		return acc;
	}, {});
};

const mapSiteMetadata = (
	siteMetadata?: SearchCombinedSiteMetadata | null,
	cloudId?: string,
	firstPartyProducts?: SearchCombinedFirstPartyProduct[],
): TSearchConfigAPIResponse['siteMetadata'] => {
	if (!siteMetadata) {
		return [];
	}

	return [
		{
			siteId: cloudId || '',
			siteName: siteMetadata.siteName || '',
			isNounsAvailable: false,
			isRovoEnabled: siteMetadata.isRovoEnabled ?? false,
			isJiraIssueIndexed: siteMetadata.isJiraIssueIndexed ?? false,
			productEdition: mapProductEdition(siteMetadata.productEdition, firstPartyProducts),
			productDataRegions: mapProductInfoToDataRegions(siteMetadata.productInfo),
			rovoFeatures: undefined,
		},
	];
};

const mapFirstPartyProductsToResources = (
	firstPartyProducts: SearchCombinedFirstPartyProduct[],
	cloudId?: string,
	siteName?: string,
): TSearchConfigAPIResponse['resources'] => {
	return firstPartyProducts.map((product) => ({
		id: `ari:cloud:${product.name}::site/${cloudId || ''}`,
		baseUrl: '',
		siteName: siteName || '',
	}));
};

const mapThirdPartyProductsToResources = (
	thirdPartyProducts: SearchCombinedThirdPartyProduct[],
): TSearch3pConfigAPIResponse => {
	return thirdPartyProducts.map((product) => ({
		type: product.name,
		outboundAuthUrl: product.outboundAuthUrl || '',
		isUserOAuthed: product.isUserOAuthed,
		providerId: product.providerId || '',
		connectionId: product.connectionId ?? undefined,
		isImpersonationAuthEnabled: product.isImpersonationAuthEnabled ?? undefined,
		integrationId: product.integrationARI
			? (product.integrationARI as unknown as T3pResource['integrationId'])
			: undefined,
		workspaceName: product.workspaceName ?? undefined,
		workspaceUrl: product.workspaceUrl ?? undefined,
		datasourceId: product.datasourceId ?? undefined,
		lightIcon: product.lightIcon ?? undefined,
		entityTypes: product.entityTypes ?? undefined,
		environmentType: product.environmentType ?? undefined,
	}));
};

export const adaptCombinedConfigToLegacy = (
	configuration: SearchCombinedConfigPayload,
	cloudId?: string,
): { firstPartyConfig: TSearchConfigAPIResponse; thirdPartyConfig: TSearch3pConfigAPIResponse } => {
	const firstPartyProducts = configuration.firstPartyProducts || [];
	const thirdPartyProducts = configuration.thirdPartyProducts || [];
	const siteMetadata = configuration.siteMetadata;

	return {
		firstPartyConfig: {
			resources: mapFirstPartyProductsToResources(
				firstPartyProducts,
				cloudId,
				siteMetadata?.siteName || '',
			),
			intercomHmac: configuration.intercomHmac || '',
			siteMetadata: mapSiteMetadata(siteMetadata, cloudId, firstPartyProducts),
		},
		thirdPartyConfig: mapThirdPartyProductsToResources(thirdPartyProducts),
	};
};
